import { useState, useEffect, useRef, useCallback } from 'react';
import SimplePeer from 'simple-peer';
import { ICE_SERVERS } from '../utils/iceConfig';

export function useWebRTC(socketRef, user) {
  const [callState, setCallState] = useState(null);
  // { callId, conversationId, callType, status, localStream, remoteStreams: { [userId]: MediaStream },
  //   participants: [{ id }], videoEnabled, audioEnabled, duration }
  const [incomingCall, setIncomingCall] = useState(null);

  const peersRef = useRef({}); // { [remoteUserId]: SimplePeer }
  const localStreamRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callIdRef = useRef(null);
  const durationStartedRef = useRef(false);

  // Cleanup helper — destroys all peers
  const cleanup = useCallback(() => {
    Object.values(peersRef.current).forEach(p => { try { p.destroy(); } catch {} });
    peersRef.current = {};
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
    durationStartedRef.current = false;
    callIdRef.current = null;
    setCallState(null);
    setIncomingCall(null);
  }, []);

  // Start duration timer (only once per call)
  const startDurationTimer = useCallback(() => {
    if (durationStartedRef.current) return;
    durationStartedRef.current = true;
    let secs = 0;
    durationIntervalRef.current = setInterval(() => {
      secs++;
      setCallState(prev => prev ? { ...prev, duration: secs } : null);
    }, 1000);
  }, []);

  // Get media stream
  const getStream = useCallback(async (callType) => {
    const constraints = {
      audio: true,
      video: callType === 'video' ? { width: { ideal: 640 }, height: { ideal: 480 } } : false,
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    localStreamRef.current = stream;
    return stream;
  }, []);

  // Play / stop ringtone
  const playRingtone = useCallback(() => {
    try {
      if (!ringtoneRef.current) {
        ringtoneRef.current = new Audio('/sounds/ringtone.wav');
        ringtoneRef.current.loop = true;
      }
      ringtoneRef.current.play().catch(() => {});
    } catch {}
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.pause();
      ringtoneRef.current.currentTime = 0;
    }
  }, []);

  // ── Create a peer for a remote user ──
  const createPeer = useCallback((remoteUserId, initiator, callId) => {
    const socket = socketRef?.current;
    if (!socket || !localStreamRef.current) return null;

    const peer = new SimplePeer({
      initiator,
      stream: localStreamRef.current,
      config: { iceServers: ICE_SERVERS },
      trickle: true,
    });

    peer.on('signal', (signal) => {
      socket.emit('call:signal', { callId, targetUserId: remoteUserId, signal });
    });

    peer.on('stream', (remoteStream) => {
      setCallState(prev => {
        if (!prev) return null;
        const streams = { ...prev.remoteStreams, [remoteUserId]: remoteStream };
        return { ...prev, remoteStreams: streams, status: 'active' };
      });
      startDurationTimer();
    });

    peer.on('error', (err) => {
      console.error(`Peer error (${remoteUserId}):`, err);
    });

    peer.on('close', () => {
      // Remove this peer's stream
      setCallState(prev => {
        if (!prev) return null;
        const streams = { ...prev.remoteStreams };
        delete streams[remoteUserId];
        return { ...prev, remoteStreams: streams };
      });
      delete peersRef.current[remoteUserId];
    });

    peersRef.current[remoteUserId] = peer;
    return peer;
  }, [socketRef, startDurationTimer]);

  // ── Initiate a call ──
  const initiateCall = useCallback(async (conversationId, callType = 'audio') => {
    const socket = socketRef?.current;
    if (!socket || callState) return;

    try {
      const stream = await getStream(callType);

      setCallState({
        callId: null,
        conversationId,
        callType,
        status: 'ringing',
        localStream: stream,
        remoteStreams: {},
        participants: [],
        videoEnabled: callType === 'video',
        audioEnabled: true,
        duration: 0,
      });

      socket.emit('call:initiate', { conversationId, callType }, (res) => {
        if (res?.error) {
          cleanup();
          if (res.error === 'busy') {
            setCallState(null);
            return 'busy';
          }
          return;
        }
        callIdRef.current = res.callId;
        setCallState(prev => prev ? { ...prev, callId: res.callId } : null);
      });
    } catch (err) {
      console.error('Failed to get media:', err);
      cleanup();
    }
  }, [socketRef, callState, getStream, cleanup]);

  // ── Accept incoming call ──
  const acceptCall = useCallback(async (callId) => {
    const socket = socketRef?.current;
    if (!socket || !incomingCall) return;

    stopRingtone();

    try {
      const stream = await getStream(incomingCall.callType);

      callIdRef.current = callId;
      setCallState({
        callId,
        conversationId: incomingCall.conversationId,
        callType: incomingCall.callType,
        status: 'connecting',
        localStream: stream,
        remoteStreams: {},
        participants: [],
        videoEnabled: incomingCall.callType === 'video',
        audioEnabled: true,
        duration: 0,
      });
      setIncomingCall(null);

      socket.emit('call:accept', { callId }, (res) => {
        if (res?.error) {
          cleanup();
          return;
        }
        // Create a non-initiator peer for each existing participant
        const existing = res.existingParticipants || [];
        for (const pid of existing) {
          createPeer(pid, false, callId);
        }
      });
    } catch (err) {
      console.error('Failed to accept call:', err);
      cleanup();
    }
  }, [socketRef, incomingCall, getStream, cleanup, stopRingtone, createPeer]);

  // ── Reject incoming call ──
  const rejectCall = useCallback((callId) => {
    const socket = socketRef?.current;
    if (!socket) return;

    stopRingtone();
    socket.emit('call:reject', { callId });
    setIncomingCall(null);
  }, [socketRef, stopRingtone]);

  // ── End active call ──
  const endCall = useCallback(() => {
    const socket = socketRef?.current;
    if (!socket || !callState?.callId) {
      cleanup();
      return;
    }

    socket.emit('call:end', { callId: callState.callId });
    cleanup();
  }, [socketRef, callState, cleanup]);

  // ── Toggle video ──
  const toggleVideo = useCallback(() => {
    if (!callState?.localStream) return;
    const videoTrack = callState.localStream.getVideoTracks()[0];

    if (videoTrack) {
      videoTrack.enabled = !videoTrack.enabled;
      setCallState(prev => prev ? { ...prev, videoEnabled: videoTrack.enabled } : null);
      socketRef?.current?.emit('call:toggle-video', {
        callId: callState.callId,
        videoEnabled: videoTrack.enabled,
      });
    } else if (callState.callType === 'audio') {
      // Upgrade to video by adding a video track
      navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 640 }, height: { ideal: 480 } } })
        .then(videoStream => {
          const track = videoStream.getVideoTracks()[0];
          callState.localStream.addTrack(track);
          // Add track to ALL peers
          Object.values(peersRef.current).forEach(peer => {
            try { peer.addTrack(track, callState.localStream); } catch {}
          });
          setCallState(prev => prev ? { ...prev, videoEnabled: true } : null);
          socketRef?.current?.emit('call:toggle-video', {
            callId: callState.callId,
            videoEnabled: true,
          });
        }).catch(() => {});
    }
  }, [callState, socketRef]);

  // ── Toggle audio ──
  const toggleAudio = useCallback(() => {
    if (!callState?.localStream) return;
    const audioTrack = callState.localStream.getAudioTracks()[0];
    if (audioTrack) {
      audioTrack.enabled = !audioTrack.enabled;
      setCallState(prev => prev ? { ...prev, audioEnabled: audioTrack.enabled } : null);
    }
  }, [callState]);

  // ── Socket event listeners ──
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    const handleIncoming = (data) => {
      if (callState) {
        socket.emit('call:reject', { callId: data.callId });
        return;
      }
      setIncomingCall(data);
      playRingtone();
    };

    // When someone accepts our call — we (initiator) create a peer for them
    const handleAccepted = ({ callId, userId: acceptedBy }) => {
      const myCallId = callIdRef.current;
      if (!myCallId || myCallId !== callId) return;
      if (acceptedBy === user?.id) return;
      // Don't create duplicate peer
      if (peersRef.current[acceptedBy]) return;

      stopRingtone();
      setCallState(prev => prev ? { ...prev, status: 'connecting' } : null);

      createPeer(acceptedBy, true, callId);
    };

    // When a new participant joins AFTER us — we create an initiator peer for them
    const handlePeerJoined = ({ callId, userId: joinedUserId }) => {
      const myCallId = callIdRef.current;
      if (!myCallId || myCallId !== callId) return;
      if (joinedUserId === user?.id) return;
      if (peersRef.current[joinedUserId]) return;

      createPeer(joinedUserId, true, callId);
    };

    // When a participant leaves — tear down their peer
    const handlePeerLeft = ({ callId, userId: leftUserId }) => {
      const myCallId = callIdRef.current;
      if (!myCallId || myCallId !== callId) return;

      const peer = peersRef.current[leftUserId];
      if (peer) {
        try { peer.destroy(); } catch {}
        delete peersRef.current[leftUserId];
      }
      setCallState(prev => {
        if (!prev) return null;
        const streams = { ...prev.remoteStreams };
        delete streams[leftUserId];
        return { ...prev, remoteStreams: streams };
      });
    };

    // Route signal to the correct peer
    const handleSignal = ({ callId, fromUserId, signal }) => {
      const peer = peersRef.current[fromUserId];
      if (peer) {
        peer.signal(signal);
      }
    };

    const handleRejected = ({ callId }) => {
      if (callState?.callId === callId || callIdRef.current === callId) {
        stopRingtone();
        cleanup();
      }
    };

    const handleEnded = ({ callId }) => {
      if (callState?.callId === callId || callIdRef.current === callId) {
        cleanup();
      }
      if (incomingCall?.callId === callId) {
        stopRingtone();
        setIncomingCall(null);
      }
    };

    const handleBusy = ({ callId }) => {
      if (callState?.callId === callId || callIdRef.current === callId) {
        cleanup();
      }
    };

    const handleVideoToggled = ({ callId, userId: uid, videoEnabled }) => {
      if ((callState?.callId === callId || callIdRef.current === callId) && uid !== user?.id) {
        setCallState(prev => {
          if (!prev) return null;
          const videoStates = { ...(prev.remoteVideoStates || {}), [uid]: videoEnabled };
          return { ...prev, remoteVideoStates: videoStates };
        });
      }
    };

    socket.on('call:incoming', handleIncoming);
    socket.on('call:accepted', handleAccepted);
    socket.on('call:peer-joined', handlePeerJoined);
    socket.on('call:peer-left', handlePeerLeft);
    socket.on('call:signal', handleSignal);
    socket.on('call:rejected', handleRejected);
    socket.on('call:ended', handleEnded);
    socket.on('call:busy', handleBusy);
    socket.on('call:video-toggled', handleVideoToggled);

    return () => {
      socket.off('call:incoming', handleIncoming);
      socket.off('call:accepted', handleAccepted);
      socket.off('call:peer-joined', handlePeerJoined);
      socket.off('call:peer-left', handlePeerLeft);
      socket.off('call:signal', handleSignal);
      socket.off('call:rejected', handleRejected);
      socket.off('call:ended', handleEnded);
      socket.off('call:busy', handleBusy);
      socket.off('call:video-toggled', handleVideoToggled);
    };
  }, [socketRef?.current, callState?.callId, incomingCall?.callId, user?.id]); // eslint-disable-line

  return {
    callState,
    incomingCall,
    initiateCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleVideo,
    toggleAudio,
  };
}
